import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format, parse } from 'date-fns';
import { ChevronDown, Calendar } from 'lucide-react';
import { FormTemplate, FormField as ImportedFormField } from '../../services/formTemplateService';
import { useTheme } from '../../contexts/ThemeContext';

// Extend the imported FormField type to include the options type we need
type FormField = Omit<ImportedFormField, 'options'> & {
  required?: boolean;
  options?: Array<{ label: string; value: string } | string>;
};

// Props for the component
interface DynamicFormRendererProps {
  formTemplate: FormTemplate | null;
  initialValues?: Record<string, any>;
  onSubmit?: (values: Record<string, any>) => void;
  submitButtonText?: string;
  loading?: boolean;
  readOnly?: boolean;
  noForm?: boolean; // Prop to prevent form nesting
}

const DynamicFormRenderer: React.FC<DynamicFormRendererProps> = ({
  formTemplate,
  initialValues = {},
  onSubmit,
  submitButtonText = 'Submit',
  loading = false,
  readOnly = false,
  noForm = false // Default to using form element
}) => {
  const { theme } = useTheme();
  const prevInitialValuesRef = useRef<string>('');

  // Helper function to get form fields
  const getFormFields = (): FormField[] => {
    if (!formTemplate) return [];
    if (Array.isArray(formTemplate.schema)) {
      return formTemplate.schema as FormField[];
    } else if (formTemplate.schema && typeof formTemplate.schema === 'object' && Array.isArray(formTemplate.schema.fields)) {
      return formTemplate.schema.fields as FormField[];
    }
    return [];
  };

  const [formValues, setFormValues] = useState<Record<string, any>>({});

  // Helper function to get the value from an option (handles both string and object options)
  const getOptionValue = (option: string | { label: string; value: string }): string => {
    return typeof option === 'string' ? option : option.value;
  };

  // Helper function to get the display label from an option
  const getOptionLabel = (option: string | { label: string; value: string }): string => {
    return typeof option === 'string' ? option : option.label || option.value;
  };

  // Update form values only when initialValues actually changes (deep comparison via JSON)
  useEffect(() => {
    if (!formTemplate) return;

    const initialValuesString = JSON.stringify(initialValues);

    // Only update if values have actually changed
    if (initialValuesString !== prevInitialValuesRef.current) {
      prevInitialValuesRef.current = initialValuesString;

      const processedValues = { ...initialValues };
      const fields = getFormFields();

      fields.forEach(field => {
        if (field.name in processedValues) {
          // Convert date strings to Date objects for date fields
          if (field.type === 'date' && processedValues[field.name]) {
            try {
              processedValues[field.name] = new Date(processedValues[field.name]);
            } catch (e) {
              console.warn(`Failed to parse date for field ${field.name}:`, processedValues[field.name]);
            }
          }
          // Ensure array values are properly formatted for checkboxes
          if (field.type === 'checkbox' && !Array.isArray(processedValues[field.name])) {
            processedValues[field.name] = processedValues[field.name] ? [processedValues[field.name]] : [];
          }
        }
      });

      setFormValues(processedValues);
    }
  }, [formTemplate, initialValues]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = getFormFields().filter(field => field.required);
    const missingFields = requiredFields.filter(field => {
      const value = formValues[field.name];
      return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
    });
    
    if (missingFields.length > 0) {
      // Highlight missing fields or show an error message
      console.warn('Missing required fields:', missingFields.map(f => f.label || f.name));
      // You might want to show an error message to the user here
      return;
    }
    
    if (onSubmit) {
      // Create a copy of form values to avoid mutating the state directly
      const submissionValues = { ...formValues };
      
      // Process each field for submission
      getFormFields().forEach(field => {
        // Format dates as ISO strings for submission
        if (field.type === 'date' && submissionValues[field.name]) {
          try {
            const date = new Date(submissionValues[field.name]);
            submissionValues[field.name] = date.toISOString();
          } catch (e) {
            console.warn(`Failed to format date for field ${field.name}:`, submissionValues[field.name]);
          }
        }
      });
      
      onSubmit(submissionValues);
    }
  };

  // Handle form values change
  const handleValueChange = (name: string, value: any) => {
    const updatedValues = {
      ...formValues,
      [name]: value
    };
    
    setFormValues(updatedValues);
    
    // If noForm is true, we're in a controlled mode where the parent handles the state
    if (onSubmit && noForm) {
      // Create a copy to avoid mutating the state directly
      const submissionValues = { ...updatedValues };
      
      // Process the changed field for submission
      const field = getFormFields().find(f => f.name === name);
      if (field?.type === 'date' && submissionValues[name]) {
        try {
          const date = new Date(submissionValues[name]);
          submissionValues[name] = date.toISOString();
        } catch (e) {
          console.warn(`Failed to format date for field ${name}:`, submissionValues[name]);
        }
      }
      
      onSubmit(submissionValues);
    }
  };

  if (!formTemplate) {
    return <div>No form template provided.</div>;
  }

  // Format date values for display/submission with Myanmar timezone (UTC+06:30)
  const formatDateValue = (value: any) => {
    if (!value) return null;
    // Format with Myanmar timezone (UTC+06:30)
    if (typeof value === 'string') {
      return value.includes('T') ? value : `${value}T00:00:00+06:30`;
    }
    return format(value, 'yyyy-MM-dd') + 'T00:00:00+06:30';
  };

  // Transform API response date values for DatePicker
  const transformInitialValue = (field: FormField, value: any) => {
    if (field.type === 'date' && value) {
      // Parse date string to Date object
      if (typeof value === 'string') {
        try {
          // Handle different date formats
          return value.includes('T') 
            ? new Date(value) 
            : parse(value, 'yyyy-MM-dd', new Date());
        } catch (e) {
          return new Date();
        }
      }
    }
    return value;
  };

  // Render form fields
  const renderFormContent = () => (
    <>
      {getFormFields().map((field: FormField) => {
        const value = formValues[field.name] !== undefined 
          ? formValues[field.name] 
          : transformInitialValue(field, initialValues[field.name]);
        
        return (
          <div key={field.name} className="mb-4">
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            {renderFormField(
              field, 
              readOnly, 
              (val) => handleValueChange(field.name, field.type === 'date' ? formatDateValue(val) : val),
              value
            )}
            {field.required && !formValues[field.name] && (
              <p className="mt-1 text-sm text-red-500">{field.label} is required</p>
            )}
          </div>
        );
      })}

      {!readOnly && onSubmit && !noForm && (
        <div className="mt-6">
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${loading ? 'opacity-70 cursor-not-allowed' : ''} ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-400 text-white'}`}
          >
            {loading ? (
              <>
                <span className="inline-block h-4 w-4 rounded-full border-2 border-t-transparent border-white animate-spin mr-2 align-middle"></span>
                <span>Loading...</span>
              </>
            ) : submitButtonText}
          </button>
        </div>
      )}
    </>
  );
  
  // Render form fields directly without form element when noForm is true
  const renderFormFieldsDirectly = () => (
    <>
      {getFormFields().map((field: FormField) => {
        const handleChange = (value: any) => {
          // Format date values if needed
          const formattedValue = field.type === 'date' ? formatDateValue(value) : value;
          
          // Update the form values
          const newValues = { ...formValues, [field.name]: formattedValue };
          setFormValues(newValues);
          
          // Call onSubmit with the updated values
          if (onSubmit) {
            onSubmit(newValues);
          }
        };
        
        const value = formValues[field.name] !== undefined 
          ? formValues[field.name] 
          : transformInitialValue(field, initialValues[field.name]);
        
        return (
          <div key={field.name} className="mb-4">
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            {renderFormField(field, readOnly, handleChange, value)}
            {field.required && !formValues[field.name] && (
              <p className="mt-1 text-sm text-red-500">{field.label} is required</p>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="dynamic-form-renderer">
      {noForm ? (
        // Render form fields directly without form wrapper to avoid nesting issues
        <div className="form-fields-container">
          {renderFormFieldsDirectly()}
        </div>
      ) : (
        // Render with form wrapper
        <form
          onSubmit={handleSubmit}
          className={`space-y-4 ${readOnly ? 'opacity-75' : ''}`}
        >
          {renderFormContent()}
        </form>
      )}
    </div>
  );
};

// Helper function to render the appropriate field based on type
const renderFormField = (field: FormField, readOnly: boolean, onChange: (value: any) => void, value: any) => {
  const { theme } = useTheme();
  const baseInputStyles = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-200 ${readOnly ? 'cursor-not-allowed opacity-75' : ''} ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-400'}`;
  
  // Helper function to get option value
  const getOptionValue = (option: string | { label: string; value: string }): string => {
    return typeof option === 'string' ? option : option.value || '';
  };
  
  // Helper function to get option label
  const getOptionLabel = (option: string | { label: string; value: string }): string => {
    return typeof option === 'string' ? option : option.label || option.value || '';
  };
  
  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          placeholder={field.placeholder || `Enter ${field.label}`}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          value={value || ''}
          className={`${baseInputStyles}`}
        />
      );
    case 'textarea':
      return (
        <textarea
          placeholder={field.placeholder || `Enter ${field.label}`}
          rows={4}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          value={value || ''}
          className={`${baseInputStyles}`}
        />
      );
    case 'select':
      return (
        <div className="relative">
          <select
            disabled={readOnly}
            onChange={(e) => onChange(e.target.value)}
            value={value || ''}
            className={`${baseInputStyles} appearance-none pr-10`}
          >
            <option value="" disabled>{field.placeholder || `Select ${field.label}`}</option>
            {field.options?.map((option, index) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              return (
                <option key={optionValue || `option-${index}`} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
            <ChevronDown className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
        </div>
      );
    case 'radio':
      return (
        <div className="space-y-2">
          {field.options?.map((option, index) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            return (
              <label key={optionValue || `option-${index}`} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value={optionValue}
                  checked={value === optionValue}
                  onChange={() => onChange(optionValue)}
                  disabled={readOnly}
                  className={`h-4 w-4 ${theme === 'dark' ? 'text-blue-500 border-gray-600' : 'text-blue-600 border-gray-300'}`}
                />
                <span className={`${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  {optionLabel}
                </span>
              </label>
            );
          })}
        </div>
      );
    case 'checkbox':
      return (
        <div className="space-y-2">
          {field.options?.map((option, index) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const isChecked = Array.isArray(value) ? value.includes(optionValue) : false;
            
            return (
              <label key={optionValue || `option-${index}`} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  value={optionValue}
                  checked={isChecked}
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? [...value] : [];
                    if (e.target.checked) {
                      onChange([...currentValues, optionValue]);
                    } else {
                      onChange(currentValues.filter(val => val !== optionValue));
                    }
                  }}
                  disabled={readOnly}
                  className={`h-4 w-4 rounded ${theme === 'dark' ? 'text-blue-500 border-gray-600' : 'text-blue-600 border-gray-300'}`}
                />
                <span className={`${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  {optionLabel}
                </span>
              </label>
            );
          })}
        </div>
      );
    case 'date':
      return (
        <div className="relative">
          <input
            type="date"
            disabled={readOnly}
            onChange={(e) => onChange(e.target.value)}
            value={value ? (typeof value === 'string' ? value.split('T')[0] : format(value, 'yyyy-MM-dd')) : ''}
            className={`${baseInputStyles} pr-10`}
          />
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
            <Calendar className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
        </div>
      );
    case 'number':
      return (
        <input
          type="number"
          placeholder={field.placeholder || `Enter ${field.label}`}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.valueAsNumber || null)}
          value={value || ''}
          className={`${baseInputStyles}`}
        />
      );
    default:
      return (
        <input
          type="text"
          placeholder={field.placeholder || `Enter ${field.label}`}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          value={value || ''}
          className={`${baseInputStyles}`}
        />
      );
  }
}



export default DynamicFormRenderer;
